/* Santa Cart Guard — validates Santa bundle without Event / Event Slot properties */
(function () {
  const CONFIG = {
    adultHandles: [
      "adult-ticket-tea",
      "adult-ticket-breakfast",
    ],
    childHandles: [
      "child-ticket-tea",
      "child-ticket-breakfast",
    ],
    // Master line is identified ONLY by these properties now:
    masterPropertyKeys: ["Adult Tickets", "Child Tickets", "Total Party Size"],
    // Where to render the error on cart page
    cartErrorContainerSelector: '[data-santa-cart-errors], .cart__footer, form[action="/cart"]',
    // Optional: maximum party size (null = no cap)
    maxPartySize: null
  };

  function onReady(fn){ if(document.readyState!=="loading") fn(); else document.addEventListener("DOMContentLoaded",fn); }

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
    if (!res.ok) throw new Error("Failed to read cart");
    return res.json();
  }

  // 🟩 Hardened: always lowercase the handle for case-insensitive comparison
  function handleOf(item) {
    return ((item && item.handle) || "").toString().toLowerCase();
  }

  function hasMasterProps(item) {
    if (!item || !item.properties) return false;
    return CONFIG.masterPropertyKeys.every(k => Object.prototype.hasOwnProperty.call(item.properties, k));
  }

  function classifyItem(item) {
    const h = handleOf(item);
    const isAdult = CONFIG.adultHandles.includes(h);
    const isChild = CONFIG.childHandles.includes(h);
    const isMaster = hasMasterProps(item);
    return { isAdult, isChild, isMaster, handle: h };
  }

  function normalizeStr(x) { return (x || "").toString().trim(); }

  function validateCartStruct(cart) {
    const errors = [];

    const groups = { master: [], adults: [], children: [] };
    (cart.items || []).forEach(it => {
      const c = classifyItem(it);
      if (c.isMaster) groups.master.push(it);
      else if (c.isAdult) groups.adults.push(it);
      else if (c.isChild) groups.children.push(it);
    });

    // 🟩 New: only run validation if any Santa-related items are present
    const santaItemCount = groups.master.length + groups.adults.length + groups.children.length;
    if (santaItemCount === 0) {
      return { ok: true, errors: [] }; // Not a Santa cart — skip validation entirely
    }

    // Master presence
    if (groups.master.length === 0) {
      errors.push("Your booking is missing the event line. Please add the event again from the product page.");
      return { ok: false, errors };
    }
    if (groups.master.length > 1) {
      errors.push("Multiple event lines detected. Please keep only one event line in your cart.");
    }

    // Don’t mix Breakfast & Afternoon Tea tickets
    const allTicketHandles = [...new Set([...groups.adults.map(handleOf), ...groups.children.map(handleOf)])];
    const mixedBreakfastAndTea =
      allTicketHandles.some(h => /breakfast/.test(h)) && allTicketHandles.some(h => /tea/.test(h));
    if (mixedBreakfastAndTea) {
      errors.push("Please do not mix Breakfast and Afternoon Tea tickets in the same order.");
    }

    // Quantities
    const adultQty = groups.adults.reduce((s, it) => s + (it.quantity || 0), 0);
    const childQty = groups.children.reduce((s, it) => s + (it.quantity || 0), 0);
    const partySize = adultQty + childQty;

    if (adultQty < 1) errors.push("Please add at least 1 Adult ticket.");
    if (childQty < 1) errors.push("Please add at least 1 Child ticket.");
    if (CONFIG.maxPartySize && partySize > CONFIG.maxPartySize) {
      errors.push(`This booking exceeds the maximum party size of ${CONFIG.maxPartySize}.`);
    }

    // Each child ticket must be quantity 1
    groups.children.forEach((it) => {
      if ((it.quantity || 0) !== 1) {
        errors.push(`Child tickets must be quantity 1 each (found ${it.quantity} for “${it.title}”).`);
      }
    });

    // Child properties completeness
    groups.children.forEach((it, idx) => {
      const p = it.properties || {};
      const name = normalizeStr(p["Child Name"]);
      const diet = normalizeStr(p["Dietary Option"]);
      const specified = normalizeStr(p["Specified"]);
      if (!name) errors.push(`Child ${idx + 1}: please provide a name.`);
      if (!diet) errors.push(`Child ${idx + 1}: please select dietary requirements.`);
      if (diet === "Other" && !specified) {
        errors.push(`Child ${idx + 1}: please specify the dietary requirements for “Other”.`);
      }
    });

    // Master properties must match actual counts
    const master = groups.master[0];
    const mp = master.properties || {};
    const mpAdults = parseInt(mp["Adult Tickets"] || "0", 10);
    const mpChildren = parseInt(mp["Child Tickets"] || "0", 10);
    const mpParty = parseInt(mp["Total Party Size"] || "0", 10);

    if (Number.isFinite(mpAdults) && mpAdults !== adultQty) {
      errors.push(`The event shows ${mpAdults} adult(s) but the cart has ${adultQty} adult ticket(s). Remove and add again from the event page.`);
    }
    if (Number.isFinite(mpChildren) && mpChildren !== childQty) {
      errors.push(`The event shows ${mpChildren} child(ren) but the cart has ${childQty} child ticket(s). Remove and add again from the event page.`);
    }
    if (Number.isFinite(mpParty) && mpParty !== partySize) {
      errors.push(`The event shows a party size of ${mpParty} but the cart has ${partySize} ticket(s) in total. Remove and add again from the event page.`);
    }

    return { ok: errors.length === 0, errors };
  }

  function renderErrorsInline(errors) {
    if (!errors || !errors.length) return;

    let target = document.querySelector(CONFIG.cartErrorContainerSelector);
    if (target && !target.matches('[data-santa-cart-errors]')) {
      const block = document.createElement("div");
      block.setAttribute("data-santa-cart-errors", "true");
      target.parentNode.insertBefore(block, target);
      target = block;
    }
    if (!target) target = document.body;

    const box = document.createElement("div");
    box.setAttribute("role", "alert");
    box.style.border = "1px solid #e00";
    box.style.background = "#fff5f5";
    box.style.color = "#b00020";
    box.style.padding = "12px";
    box.style.borderRadius = "10px";
    box.style.margin = "12px 0";

    const title = document.createElement("strong");
    title.textContent = "Please fix the following before checking out:";
    title.style.display = "block";
    title.style.marginBottom = "6px";
    box.appendChild(title);

    const ul = document.createElement("ul");
    ul.style.margin = "0";
    ul.style.paddingLeft = "18px";
    errors.forEach(msg => {
      const li = document.createElement("li");
      li.textContent = msg;
      ul.appendChild(li);
    });
    box.appendChild(ul);

    target.prepend(box);
    box.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function guardCheckout(ev) {
    try {
      document.querySelectorAll('[data-santa-cart-errors]').forEach(n => (n.innerHTML = ""));
      const cart = await getCart();
      const result = validateCartStruct(cart);
      if (!result.ok) {
        ev && ev.preventDefault();
        renderErrorsInline(result.errors);
        return false;
      }
      return true;
    } catch (e) {
      ev && ev.preventDefault();
      renderErrorsInline(["We couldn’t validate your cart. Please refresh the page and try again."]);
      return false;
    }
  }

  function hookCartPage() {
    // Cart page form
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener("submit", function (e) {
        const submitter = e.submitter || document.activeElement;
        const isCheckout = submitter && ((submitter.name === "checkout") || /checkout/i.test(submitter.value || ""));
        if (isCheckout) {
          e.preventDefault();
          guardCheckout(e).then(ok => { if (ok) cartForm.submit(); });
        }
      }, true);
    }

    // Direct checkout links/buttons (drawers)
    document.body.addEventListener("click", function (e) {
      const a = e.target.closest('a[href="/checkout"], a[href^="/checkout?"], button[name="checkout"]');
      if (a) {
        e.preventDefault();
        guardCheckout(e).then(ok => { if (ok) window.location.href = a.getAttribute("href") || "/checkout"; });
      }
    }, true);
  }

  onReady(hookCartPage);
})();
